import { roundDecimal } from '@autosign/utils'
import { useMouse } from '@uidotdev/usehooks'
import { nanoid } from 'nanoid'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PDField, usePDFStore } from '../../../stores/pdf.store'
import { decodeBoundaryKey } from '../../../utils/boundaryKey'
import { getColorHex } from '../../../utils/getColorHex'
import { PDF_SCROLLABLE_CONTAINER_ID } from '../../PDFViewer'
import {
  FieldItem,
  FieldItemIcon,
  FieldItemLabel,
  fieldItemDefaultDimensions,
} from './FieldItem'
import { FORM_DEFAULT_STATE } from 'react-hook-form/dist/constants'

const FIELD_DEFAULTS = {
  fontSize: 9,
  fontFamily: 'helvetica' as const,
  fontColor: '#000000',
}

const getScrollOffsets = () => {
  const scrollableContainer = window.document.getElementById(
    PDF_SCROLLABLE_CONTAINER_ID
  )!

  return {
    x: scrollableContainer.scrollLeft,
    y: scrollableContainer.scrollTop,
  }
}

export const CursorField = () => {
  const [scrollCords, setScrollCords] = useState(() => getScrollOffsets())
  const currentRecipientId = usePDFStore((state) => state.currentRecipientId)
  const addField = usePDFStore((state) => state.addField)
  const boundaries = usePDFStore((state) => state.boundaries)
  const zoom = usePDFStore((state) => state.zoom)
  const debug = usePDFStore((state) => state.debug)
  const update = usePDFStore((state) => state.update)
  const focusedBlock = usePDFStore((state) => state.focusedBlock)
  const currentViewportPageId = usePDFStore(
    (state) => state.currentViewportPageId
  )
  const [mouse, ref] = useMouse<HTMLDivElement>()

  const mouseRect = useMemo(() => {
    return {
      withoutScrollX: mouse.x,
      withoutScrollY: mouse.y,
      x: mouse.x + scrollCords.x,
      y: mouse.y + scrollCords.y,
      width: fieldItemDefaultDimensions.width,
      height: fieldItemDefaultDimensions.height,
    }
  }, [mouse, zoom, scrollCords])

  const intersectingEval = useMemo(() => {
    const scrollableContainer = window.document.getElementById(
      PDF_SCROLLABLE_CONTAINER_ID
    )!

    const intersectedPage = Object.entries(boundaries).find(([_, pageRect]) => {
      const elementLeft = pageRect.left
      const elementTop = pageRect.top

      const isIntersectingPage =
        mouseRect.x >= elementLeft &&
        mouseRect.x <= elementLeft + pageRect.width &&
        mouseRect.y >= elementTop &&
        mouseRect.y <= elementTop + pageRect.height

      const viewportRect = scrollableContainer.getBoundingClientRect()

      const isMouseInViewport =
        mouseRect.withoutScrollX >= viewportRect.left &&
        mouseRect.withoutScrollX <= viewportRect.left + viewportRect.width &&
        mouseRect.withoutScrollY >= viewportRect.top &&
        mouseRect.withoutScrollY <= viewportRect.top + viewportRect.height

      return isIntersectingPage && isMouseInViewport
    })

    if (intersectedPage) {
      const [boundaryKey, boundary] = intersectedPage

      const { document_id, pageId } = decodeBoundaryKey(boundaryKey)

      return {
        isIntersecting: true as const,
        intersectedPage: {
          document_id,
          pageId,
          boundary,
          pageIndex: boundary.pageIndex,
        },
      }
    }

    return {
      isIntersecting: false as const,
    }
  }, [mouseRect, boundaries, zoom, currentViewportPageId])

  const intersectingEvalRef = useRef<typeof intersectingEval>(undefined)
  intersectingEvalRef.current = intersectingEval

  const mouseRectRef = useRef<typeof mouseRect>(undefined)
  mouseRectRef.current = mouseRect

  useEffect(() => {
    const scrollableContainer = window.document.getElementById(
      PDF_SCROLLABLE_CONTAINER_ID
    )!

    const callback = () => setScrollCords(getScrollOffsets())

    scrollableContainer.addEventListener('scroll', callback)

    return () => scrollableContainer.removeEventListener('scroll', callback)
  }, [])

  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      if (
        !intersectingEvalRef.current ||
        !intersectingEvalRef.current.intersectedPage ||
        !mouseRectRef.current
      ) {
        console.log('some references are undefined, skipping click event')
        return
      }

      const defaultValue = focusedBlock!.defaultValue()

      const field = {
        document_id: intersectingEvalRef.current.intersectedPage.document_id,
        page: intersectingEvalRef.current.intersectedPage.pageIndex,
        height: roundDecimal(
          (fieldItemDefaultDimensions.height /
            intersectingEvalRef.current.intersectedPage.boundary.height) *
            100
        ),
        width: roundDecimal(
          (fieldItemDefaultDimensions.width /
            intersectingEvalRef.current.intersectedPage.boundary.width) *
            100
        ),
        positionX: roundDecimal(
          ((mouseRectRef.current.x -
            intersectingEvalRef.current.intersectedPage.boundary.left) /
            intersectingEvalRef.current.intersectedPage.boundary.width) *
            100
        ),
        positionY: roundDecimal(
          ((mouseRectRef.current.y -
            intersectingEvalRef.current.intersectedPage.boundary.top) /
            intersectingEvalRef.current.intersectedPage.boundary.height) *
            100
        ),
        type: focusedBlock!.type,
        label: focusedBlock!.label,
        recipient_id: currentRecipientId,
        id: nanoid(),
        external_id: nanoid(),
        italic: false,
        is_required: false,
        bold: false,
        font_family: FIELD_DEFAULTS.fontFamily,
        font_size: FIELD_DEFAULTS.fontSize,
        font_color: FIELD_DEFAULTS.fontColor,
        helper_text: '',
        alignment: 'left',
        ...defaultValue,
      } as PDField

      addField(field)
    }

    window.document.addEventListener('click', handleClick)

    return () => {
      window.document.removeEventListener('click', handleClick)
    }
  }, [focusedBlock, currentRecipientId, zoom])

  useEffect(() => {
    update('isIntersectingPage', intersectingEval.isIntersecting)
  }, [intersectingEval.isIntersecting])

  const color = usePDFStore(
    (state) =>
      state.recipients.find((r) => r.id === state.currentRecipientId)!.color
  )

  return (
    <>
      <div id='cursor-field' ref={ref}>
        {intersectingEval.isIntersecting && (
          <FieldItem
            style={{
              position: 'absolute',
              left: mouse.elementX,
              top: mouse.elementY,
              color: color === 'black' ? 'white' : undefined,
              background: getColorHex(color),
              '--tw-ring-color': getColorHex(color, '800'),
            }}
            className='pointer-events-none cursor-none'
            fontColor={FIELD_DEFAULTS.fontColor}
            fontFamily={FIELD_DEFAULTS.fontFamily}
            fontSize={FIELD_DEFAULTS.fontSize}
          >
            <FieldItemLabel>
              {debug ? (
                <p>
                  x: {mouseRect.x}, y: {mouseRect.y}
                </p>
              ) : (
                <>
                  <FieldItemIcon type={focusedBlock!.type} />
                  {focusedBlock!.label}
                </>
              )}
            </FieldItemLabel>
          </FieldItem>
        )}
      </div>
    </>
  )
}
